<script setup>
import { Link } from '@inertiajs/vue3'
import { ref } from 'vue'
import Pagination from '@/Shared/Pagination.vue'
import Portrait from '@/Shared/Portrait.vue'
import SearchInput from '@/Shared/SearchInput.vue'
import Icon from '@/Shared/Icon.vue'
import { useInitials } from '@/Composables/useInitials'

const { getInitials } = useInitials()

const props = defineProps({
  rankings: {
    type: Object,
    default: () => ({})
  },
  filters: {
    type: Object,
    default: () => ({ search: '' })
  }
})

const search = ref(props.filters.search)
const breadcrumbs = [{ label: 'Rankings' }]
</script>

<template>
  <Portrait title="Rankings" subtitle="Gestiona los rankings del club" :breadcrumbs="breadcrumbs">
    <!-- Barra de herramientas superior -->
    <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <SearchInput
        v-model="search"
        class="w-full sm:w-64"
        placeholder="Buscar rankings..."
        url="/rankings"
        :debounce-time="250"
        :only="['rankings']"
      />
      <div class="flex space-x-3 items-stretch">
        <Link class="btn-indigo flex items-center justify-center w-full" href="/rankings/create">
          <Icon name="Plus" class="h-5 w-5 mr-2" />
          <span>Nuevo ranking</span>
        </Link>
      </div>
    </div>
    <!-- Lista de rankings con diseño de tarjetas -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div
        v-for="ranking in rankings.data"
        :key="ranking.id"
        class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md hover:border-primary-300 cursor-pointer"
        @click="$inertia.visit(`/rankings/${ranking.id}/edit`)"
      >
        <!-- Cabecera de la tarjeta -->
        <div class="p-5 border-b border-gray-100 flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div
              class="rounded-full h-12 w-12 flex items-center justify-center font-bold bg-primary-500 text-white"
            >
              {{ getInitials(ranking.name) }}
            </div>
            <div>
              <h3 class="font-semibold text-secondary-900">
                {{ ranking.name }}
              </h3>
              <div class="text-xs text-secondary-500">ID: {{ ranking.id }}</div>
            </div>
          </div>
        </div>
        <!-- Pie de la tarjeta -->
        <div class="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end">
          <span class="text-primary-600 text-sm font-medium flex items-center">
            Editar
            <Icon name="Pencil" class="h-4 w-4 ml-1" />
          </span>
        </div>
      </div>
      <!-- Estado vacío -->
      <div
        v-if="rankings.data.length === 0"
        class="col-span-full bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center"
      >
        <div class="flex flex-col items-center justify-center max-w-md mx-auto">
          <div class="bg-primary-50 p-4 rounded-full mb-4">
            <Icon name="Warning" class="h-10 w-10 text-primary-400" />
          </div>
          <h3 class="text-xl font-semibold text-secondary-900 mb-2">No hay rankings todavía</h3>
          <p class="text-secondary-500 mb-6">
            Crea un nuevo ranking para comenzar a gestionar tus competiciones
          </p>
          <Link class="btn-indigo flex items-center justify-center" href="/rankings/create">
            <Icon name="Plus" class="h-5 w-5 mr-2" />
            <span>Nuevo ranking</span>
          </Link>
        </div>
      </div>
    </div>
    <Pagination class="mt-6" :links="rankings.links" :preserve-state="true" :only="['rankings']" />
  </Portrait>
</template>
